import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { resolveUrl } from "@/lib/content";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { FileUpload } from "@/pages/admin/FileUpload";

export const ImagePickerDialog = ({ open, onClose, onSelect, title = "Choose a photo", allowUpload = true }) => {
  const [images, setImages] = useState([]);
  const [url, setUrl] = useState("");

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/public/gallery`).then((res) => setImages(res.data || [])).catch(() => setImages([]));
  }, [open]);

  const pick = (img) => { onSelect({ url: img.url, alt: img.alt || "" }); onClose(); };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="image-picker-dialog" className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-charcoal">{title}</DialogTitle>
          <DialogDescription className="font-body text-stone-grey">Pick from your gallery{allowUpload ? ", upload a new photo, or paste a link" : ""}.</DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="gallery">
          <TabsList>
            <TabsTrigger value="gallery" data-testid="image-picker-tab-gallery">Gallery</TabsTrigger>
            {allowUpload && <TabsTrigger value="upload" data-testid="image-picker-tab-upload">Upload</TabsTrigger>}
            {allowUpload && <TabsTrigger value="url" data-testid="image-picker-tab-url">Link</TabsTrigger>}
          </TabsList>
          <TabsContent value="gallery">
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3 max-h-[50vh] overflow-y-auto p-1" data-testid="image-picker-grid">
              {images.map((img) => (
                <button key={img.id} type="button" onClick={() => pick(img)} data-testid={`image-picker-item-${img.id}`}
                  className="rounded-lg overflow-hidden ring-2 ring-transparent hover:ring-jade-deep focus:ring-jade-deep transition-shadow">
                  <img src={resolveUrl(img.url)} alt={img.alt} className="w-full aspect-square object-cover" />
                </button>
              ))}
              {images.length === 0 && <p className="col-span-full font-body text-sm text-taupe py-8 text-center">No gallery photos yet.</p>}
            </div>
          </TabsContent>
          {allowUpload && (
            <TabsContent value="upload">
              <FileUpload onUpload={(u) => { onSelect({ url: u, alt: "" }); onClose(); }} label="Upload a photo from your device" />
            </TabsContent>
          )}
          {allowUpload && (
            <TabsContent value="url">
              <div className="flex gap-2">
                <Input data-testid="image-picker-url-input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://example.com/photo.jpg" className="form-input" />
                <Button type="button" className="btn-primary" disabled={!url} data-testid="image-picker-url-submit" onClick={() => { onSelect({ url, alt: "" }); setUrl(""); onClose(); }}>Use</Button>
              </div>
            </TabsContent>
          )}
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
